// components/Payment/PaymentModal.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePurchase } from '../../context/PurchaseContext';
import LoadingSpinner from '../common/LoadingSpinner';
import { formatCurrency } from '../../utils/dataHelpers';
import '../../styles/Payment.css';

const PaymentModal = ({ 
  isOpen, 
  onClose, 
  templateId, 
  onSuccess, 
  contractId = null, 
  redirectAfterSuccess = false 
}) => {
  // =============================================================================
  // ХУКИ И КОНТЕКСТ
  // =============================================================================
  const { user } = useAuth();
  const { 
    getTemplateById, 
    createPayment, 
    loadUserContracts, 
    clearError, 
    error: purchaseError, 
    paymentsLoading 
  } = usePurchase();
  const navigate = useNavigate();

  // =============================================================================
  // ЛОКАЛЬНОЕ СОСТОЯНИЕ
  // =============================================================================
  const [step, setStep] = useState('payment'); // payment | processing | success | error
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [autoCloseTimer, setAutoCloseTimer] = useState(0);
  const [paymentData, setPaymentData] = useState(null);

  // =============================================================================
  // ДАННЫЕ ШАБЛОНА
  // =============================================================================
  const template = getTemplateById(templateId);

  // =============================================================================
  // ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ
  // =============================================================================
  
  /**
   * Получить читаемое название шаблона
   */
  const getTemplateName = useCallback(() => {
    if (!template) return 'Шаблон договора';
    
    const templateNames = {
      'rent_apartment': 'Договор аренды квартиры',
      'template_rent': 'Договор аренды', 
      'loan_agreement': 'Договор займа',
      'employment_contract': 'Трудовой договор',
      'service_agreement': 'Договор оказания услуг',
      'sales_contract': 'Договор купли-продажи',
      'partnership_agreement': 'Договор партнерства'
    };
    
    return templateNames[template.id] || template.name || 'Шаблон договора';
  }, [template]);

  /**
   * Получить правильное склонение дней
   */
  const formatDuration = useCallback(() => {
    const days = template?.duration || 7;
    
    if (days === 1) return '1 день';
    if (days >= 2 && days <= 4) return `${days} дня`;
    return `${days} дней`;
  }, [template]);

  /**
   * Валидация email
   */
  const isValidEmail = useCallback((email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }, []);

  // =============================================================================
  // ОБРАБОТЧИКИ СОБЫТИЙ
  // =============================================================================
  
  /**
   * Закрытие модального окна
   */
  const handleClose = useCallback(() => {
    if (paymentsLoading) return;
    
    if (step === 'success' && redirectAfterSuccess) {
      navigate('/dashboard');
      return;
    }
    
    onClose();
  }, [paymentsLoading, step, redirectAfterSuccess, navigate, onClose]);

  /**
   * Инициация платежа
   */
  const handlePayment = useCallback(async () => {
    // Валидация
    if (!email.trim()) {
      setError('Введите email для отправки чека');
      return;
    }

    if (!isValidEmail(email.trim())) {
      setError('Введите корректный email адрес');
      return;
    }

    if (!template) {
      setError('Шаблон не найден');
      return;
    }

    try {
      setError('');
      setStep('processing');
      
      console.log('Создание платежа для шаблона:', template.id);
      
      // Создаем платеж
      const payment = await createPayment(template.id, email.trim());
      
      if (!payment || !payment.confirmationUrl) {
        throw new Error('Не удалось получить ссылку для оплаты');
      }

      console.log('Платеж создан:', payment);
      setPaymentData(payment);

      // Сохраняем данные для восстановления состояния
      const paymentInfo = {
        paymentId: payment.paymentId,
        templateId: template.id,
        contractId: contractId,
        email: email.trim(),
        timestamp: Date.now()
      };
      
      localStorage.setItem('pending_payment', JSON.stringify(paymentInfo));
      
      // Перенаправляем на оплату
      window.location.href = payment.confirmationUrl;
      
    } catch (err) {
      console.error('Ошибка создания платежа:', err);
      setError(err.message || 'Произошла ошибка при создании платежа');
      setStep('error');
    }
  }, [email, template, contractId, createPayment, isValidEmail]);

  /**
   * Повторная попытка оплаты
   */
  const handleRetry = useCallback(() => {
    setStep('payment');
    setError('');
    clearError();
  }, [clearError]);

  /**
   * Переход в личный кабинет
   */
  const handleGoToDashboard = useCallback(() => {
    onClose();
    navigate('/dashboard');
  }, [navigate, onClose]);

  /**
   * Продолжить редактирование договора
   */
  const handleContinueEditing = useCallback(() => {
    if (!contractId) {
      handleGoToDashboard();
      return;
    }

    const contractRoutes = {
      'rent_apartment': '/contract/rent',
      'template_rent': '/contract/rent',
      'loan_agreement': '/contract/loan',
      'employment_contract': '/contract/employment',
      'service_agreement': '/contract/service'
    };

    const route = contractRoutes[templateId] || '/contract/rent';
    navigate(`${route}?contractId=${contractId}&edit=true`);
  }, [contractId, templateId, navigate, handleGoToDashboard]);

  // =============================================================================
  // ЭФФЕКТЫ
  // =============================================================================
  
  /**
   * Инициализация при открытии модалки
   */
  useEffect(() => {
    if (!isOpen) return;

    setStep('payment');
    setEmail(user?.email || '');
    setError('');
    setPaymentData(null);
    setAutoCloseTimer(0);
    
    // Вызываем clearError один раз при открытии
    if (clearError) clearError();
    
    console.log('PaymentModal открыта для шаблона:', templateId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, templateId]); // Убираем зависимости, которые могут меняться во время ввода

  /**
   * Обработка успешной оплаты
   */
  useEffect(() => {
    if (step !== 'success') return;

    // Обновляем контракты пользователя
    loadUserContracts().catch(console.warn);

    // Уведомляем родительский компонент
    if (onSuccess) {
      onSuccess(template, paymentData);
    }

    // Запускаем автозакрытие
    setAutoCloseTimer(10);
  }, [step, loadUserContracts, onSuccess, template, paymentData]);

  /**
   * Таймер автозакрытия
   */
  useEffect(() => {
    if (autoCloseTimer <= 0) return;

    if (autoCloseTimer === 1) {
      handleClose();
      return;
    }

    const timer = setTimeout(() => {
      setAutoCloseTimer(prev => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [autoCloseTimer, handleClose]);

  /**
   * Отслеживание ошибок из контекста
   */
  useEffect(() => {
    if (purchaseError && step === 'processing') {
      setError(purchaseError);
      setStep('error');
    }
  }, [purchaseError, step]);

  // =============================================================================
  // РЕНДЕР ШАГОВ
  // =============================================================================
  
  /**
   * Форма оплаты
   */
  const renderPaymentForm = () => {
    const isEmailValid = isValidEmail(email.trim());
    const showEmailError = email.trim() !== '' && !isEmailValid;

    return (
      <div className="paywall-premium-card">
        {/* Заголовок с мягким фиолетовым акцентом */}
        <div className="paywall-card-header">
          <h1 className="paywall-title">
            {getTemplateName()}
          </h1>
          <p className="paywall-subtitle">
            Доступ к редактированию и скачиванию в Word и PDF
          </p>
        </div>

        <div className="paywall-card-body">
          {/* Преимущества */}
          <div className="paywall-benefits">
            <div className="benefit-item">
              <span className="benefit-icon">✓</span>
              <span>Доступ на {formatDuration()}</span>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon">✓</span>
              <span>Безлимитные правки и скачивания</span>
            </div>
          </div>

          {/* Цена */}
          <div className="paywall-price-container">
            <div className="paywall-price-value">
              {formatCurrency(template?.price || 0)}
            </div>
            <div className="paywall-price-label">разовый платеж</div>
          </div>

          {/* Ввод Email */}
          <div className="paywall-input-section">
            <div className="paywall-input-container">
              <label className="paywall-label">Email для получения доступа</label>
              <div className={`paywall-input-wrapper ${showEmailError ? 'has-error' : ''} ${isEmailValid ? 'is-valid' : ''}`}>
                <span className="input-icon">✉</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="paywall-input"
                  placeholder="name@example.com"
                  disabled={paymentsLoading}
                  autoFocus
                />
              </div>
              {showEmailError && (
                <div className="paywall-input-error-msg">
                  Введите корректный email
                </div>
              )}
            </div>
          </div>

          <button
            className="paywall-submit-btn"
            onClick={handlePayment}
            disabled={!isEmailValid || paymentsLoading}
          >
            {paymentsLoading ? (
              <LoadingSpinner size="small" />
            ) : (
              <>
                <span>Оплатить доступ</span>
                <span className="btn-arrow">→</span>
              </>
            )}
          </button>

          <div className="paywall-security">
            <span>🔒 Безопасная оплата через YooMoney</span>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Процесс оплаты
   */
  const renderProcessing = () => (
    <div className="payment-processing-content">
      <div className="payment-processing-spinner">
        <LoadingSpinner size="large" />
      </div>
      <h2 className="payment-processing-title">
        Переход к оплате
      </h2>
      <p className="payment-processing-subtitle">
        Сейчас вы будете перенаправлены на страницу YooMoney...
      </p>
    </div>
  );

  /**
   * Успешная оплата
   */
  const renderSuccess = () => (
    <div className="payment-success-content">
      <div className="payment-success-icon">🎉</div>
      <h2 className="payment-success-title">Оплата прошла успешно!</h2>
      <p className="payment-success-subtitle">
        Шаблон "{getTemplateName()}" теперь доступен для использования.
      </p>
      
      <div className="payment-success-actions">
        {contractId ? (
          <button 
            className="payment-success-btn-primary" 
            onClick={handleContinueEditing}
          >
            Продолжить работу
          </button>
        ) : (
          <button 
            className="payment-success-btn-primary" 
            onClick={handleGoToDashboard}
          >
            В личный кабинет
          </button>
        )}
        <button 
          className="payment-success-btn-secondary" 
          onClick={handleClose}
        >
          Закрыть
        </button>
      </div>
    </div>
  );

  /**
   * Ошибка оплаты
   */
  const renderError = () => (
    <div className="payment-error-content">
      <div className="payment-error-icon">❌</div>
      <h2 className="payment-error-title">Ошибка оплаты</h2>
      <p className="payment-error-subtitle">
        {error || 'Не удалось провести платеж. Попробуйте еще раз.'}
      </p>

      <div className="payment-error-actions">
        <button 
          className="payment-success-btn-primary" 
          onClick={handleRetry}
        >
          Попробовать снова
        </button>
        <button 
          className="payment-success-btn-secondary" 
          onClick={handleClose}
        >
          Закрыть
        </button>
      </div>
    </div>
  );

  // =============================================================================
  // ОСНОВНОЙ РЕНДЕР
  // =============================================================================
  if (!isOpen || !template) {
    return null;
  }

  return (
    <div 
      className="payment-modal-overlay" 
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
    >
      <div 
        className="payment-modal" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Кнопка закрытия */}
        {step !== 'processing' && (
          <button 
            className="payment-close" 
            onClick={handleClose}
            aria-label="Закрыть"
            disabled={paymentsLoading}
          >
            ×
          </button>
        )}
        
        {/* Контент */}
        <div className="payment-content">
          {step === 'payment' && renderPaymentForm()}
          {step === 'processing' && renderProcessing()}
          {step === 'success' && renderSuccess()}
          {step === 'error' && renderError()}
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
